/**
 * Mesh radios: one link carrying several aircraft, each made its own drone.
 *
 * A meshed RFD900x on this laptop is shared from the browser. A radio the
 * server can reach (a UDP port relays send to, MAVProxy over TCP, a serial port
 * on the server) is opened by the server. Either way every aircraft on it
 * joins your team, and each running mesh shows the chain from radio to drone
 * stage by stage, so an empty mesh says where it stopped.
 */

import { useCallback, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import {
  adoptMeshVehicle,
  listMeshes,
  startMesh,
  stopMesh,
  type ChainStage,
  type Mesh,
  type MeshVehicle,
} from '@/api/mesh';

import {
  browserChain,
  meshBridgeState,
  mergeChain,
  startMeshBridge,
  stopMeshBridge,
  subscribeMeshBridge,
} from './meshBridgeClient';
import { BAUD_RATES, describe as describeBridgeError } from './serialBridgeClient';
import { webSerialSupported } from './webSerialBridge';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

const POLL_MS = 2000;
const MESH_ID = /^[A-Za-z0-9_.-]{1,40}$/;

const STAGE_LABEL: Record<string, string> = {
  port: 'PORT',
  bytes: 'BYTES',
  frames: 'FRAMES',
  heartbeats: 'AIRCRAFT',
  return: 'RETURN',
};

const STATE_COLOR: Record<string, string> = {
  adopted: OK,
  pending: WARN,
  released: MUTED,
  refused: DANGER,
};

function ago(seconds: number | null): string {
  if (seconds === null) return 'never';
  if (seconds < 2) return 'now';
  if (seconds < 120) return `${Math.round(seconds)} s ago`;
  return `${Math.round(seconds / 60)} min ago`;
}

export function MeshPanel(): ReactNode {
  const bridge = useSyncExternalStore(subscribeMeshBridge, meshBridgeState);
  const [meshes, setMeshes] = useState<Mesh[]>([]);
  const [meshId, setMeshId] = useState('mesh');
  const [baudRate, setBaudRate] = useState<number>(BAUD_RATES[0]);
  const [connectionString, setConnectionString] = useState('udpin:0.0.0.0:14550');
  const [heartbeatHosts, setHeartbeatHosts] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const result = await listMeshes();
    if (result.ok) setMeshes(result.data);
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const validId = MESH_ID.test(meshId.trim());

  const share = async () => {
    setError(null);
    setBusy(true);
    try {
      await startMeshBridge({ meshId: meshId.trim(), baudRate });
      await refresh();
    } catch (failure) {
      setError(describeBridgeError(failure));
    }
    setBusy(false);
  };

  const open = async () => {
    setError(null);
    setBusy(true);
    const hosts = heartbeatHosts
      .split(',')
      .map((h) => h.trim())
      .filter((h) => h !== '');
    const result = await startMesh({
      meshId: meshId.trim(),
      connectionString: connectionString.trim(),
      heartbeatHosts: hosts,
    });
    setBusy(false);
    if (result.ok) await refresh();
    else setError(result.error.message);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10.5px] leading-relaxed text-white/55">
        One radio, several aircraft: a meshed RFD900 or MAVProxy fanning a fleet into one port.
        Each aircraft becomes its own drone, named after the mesh and its system id, in your team.
        Every aircraft needs its own SYSID_THISMAV; aircraft left on the default of 1 look like one.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8px]">MESH NAME</span>
          <input
            value={meshId}
            onChange={(event) => setMeshId(event.target.value)}
            placeholder="alpha"
            className="well h-9 rounded-[10px] px-3 font-mono text-[12px] outline-none"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8px]">BAUD (USB)</span>
          <select
            value={baudRate}
            onChange={(event) => setBaudRate(Number.parseInt(event.target.value, 10))}
            disabled={bridge.running}
            className="well h-9 rounded-[10px] px-2 font-mono text-[12px] outline-none disabled:opacity-40"
          >
            {BAUD_RATES.map((rate) => (
              <option key={rate} value={rate}>
                {rate}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-1.5 rounded-[12px] border border-white/8 bg-black/25 p-3">
        <span className="label-mono text-[8px]">A RADIO ON THIS LAPTOP</span>
        {webSerialSupported() ? (
          bridge.running ? (
            <button
              type="button"
              onClick={() => void stopMeshBridge()}
              className="h-9 rounded-full border font-mono text-[10px] font-bold tracking-wide"
              style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
            >
              STOP SHARING {bridge.meshId.toUpperCase()}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void share()}
              disabled={busy || !validId}
              className="h-9 rounded-full bg-white/90 font-mono text-[10px] font-bold tracking-wide text-[#0c0c0d] disabled:opacity-40"
            >
              {busy ? 'OPENING…' : 'CHOOSE PORT AND SHARE'}
            </button>
          )
        ) : (
          <p className="text-[10.5px] leading-relaxed text-white/55">
            This browser cannot open a serial port. Use Chrome or Edge, or run{' '}
            <span className="font-mono">mesh_relay.py</span> from the Parallax Bridge folder on this
            laptop.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5 rounded-[12px] border border-white/8 bg-black/25 p-3">
        <span className="label-mono text-[8px]">A LINK THE SERVER CAN REACH</span>
        <input
          value={connectionString}
          onChange={(event) => setConnectionString(event.target.value)}
          placeholder="udpin:0.0.0.0:14550,14551"
          className="well h-9 rounded-[10px] px-3 font-mono text-[12px] outline-none"
        />
        <input
          value={heartbeatHosts}
          onChange={(event) => setHeartbeatHosts(event.target.value)}
          placeholder="Say hello first to (UDP only): 10.0.0.20:14550"
          className="well h-9 rounded-[10px] px-3 font-mono text-[11px] outline-none"
        />
        <button
          type="button"
          onClick={() => void open()}
          disabled={busy || !validId}
          className="h-9 rounded-full border border-white/20 font-mono text-[10px] font-bold tracking-wide text-white/85 disabled:opacity-40"
        >
          START MESH
        </button>
      </div>

      {error ?? bridge.error ? (
        <div className="font-mono text-[10.5px]" style={{ color: WARN }}>
          {error ?? bridge.error}
        </div>
      ) : null}

      {meshes.map((mesh) => (
        <MeshCard
          key={mesh.meshId}
          mesh={mesh}
          browser={
            bridge.running && bridge.meshId === mesh.meshId ? browserChain(bridge.status) : null
          }
          onChanged={() => void refresh()}
        />
      ))}
    </div>
  );
}

function MeshCard({
  mesh,
  browser,
  onChanged,
}: {
  mesh: Mesh;
  browser: ChainStage[] | null;
  onChanged: () => void;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const chain = browser === null ? mesh.chain : mergeChain(browser, mesh.chain);
  const firstBroken = chain.find((stage) => !stage.ok);
  const now = Date.now() / 1000;

  const stop = async () => {
    const result = await stopMesh(mesh.meshId);
    if (result.ok) onChanged();
    else setError(result.error.message);
  };
  const adopt = async (vehicle: MeshVehicle) => {
    const result = await adoptMeshVehicle(mesh.meshId, vehicle.sysid);
    if (result.ok) onChanged();
    else setError(result.error.message);
  };

  return (
    <div className="flex flex-col gap-2 rounded-[12px] border border-white/10 bg-black/30 p-3">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px] font-semibold">{mesh.meshId}</span>
        <span className="font-mono text-[9.5px]" style={{ color: MUTED }}>
          {mesh.origin} · {mesh.label} · {mesh.startedBy}
        </span>
        {mesh.origin === 'server' ? (
          <button
            type="button"
            onClick={() => void stop()}
            className="ml-auto font-mono text-[9.5px] font-bold"
            style={{ color: DANGER }}
          >
            STOP
          </button>
        ) : null}
      </div>

      <div className="flex gap-1">
        {chain.map((stage) => (
          <span
            key={stage.key}
            title={stage.detail}
            className="flex-1 rounded-[6px] px-1.5 py-1 text-center font-mono text-[8.5px] font-bold"
            style={{
              color: stage.ok ? OK : DANGER,
              background: `color-mix(in oklch, ${stage.ok ? OK : DANGER} 12%, transparent)`,
            }}
          >
            {STAGE_LABEL[stage.key] ?? stage.key}
          </span>
        ))}
      </div>
      <div className="font-mono text-[10px]" style={{ color: firstBroken ? WARN : MUTED }}>
        {firstBroken
          ? firstBroken.detail
          : `${mesh.onlineCount} online, telemetry at 1/${mesh.streamDivisor} rate to share the channel`}
      </div>
      {mesh.sameSysidSuspected ? (
        <div className="font-mono text-[10px]" style={{ color: WARN }}>
          Only one system id is on this radio. If you expected several aircraft, they are probably
          all on SYSID_THISMAV 1 and cannot be told apart. Give each its own.
        </div>
      ) : null}
      {mesh.error ? (
        <div className="font-mono text-[10px]" style={{ color: DANGER }}>
          {mesh.error}
        </div>
      ) : null}

      {mesh.vehicles.length > 0 ? (
        <div className="flex flex-col gap-1">
          {mesh.vehicles.map((vehicle) => (
            <div key={vehicle.sysid} className="flex items-center gap-2 font-mono text-[10.5px]">
              <span
                className="size-[7px] shrink-0 rounded-full"
                style={{ background: vehicle.online ? OK : DANGER }}
              />
              <span className="w-[54px]">sys {vehicle.sysid}</span>
              <span className="w-[110px] min-w-0 shrink truncate" title={vehicle.droneId ?? undefined}>{vehicle.droneId ?? '·'}</span>
              <span style={{ color: STATE_COLOR[vehicle.state] ?? MUTED }}>{vehicle.state}</span>
              <span className="tnum ml-auto" style={{ color: MUTED }}>
                {vehicle.frames} frames ·{' '}
                {ago(vehicle.lastHeartbeatAt === null ? null : now - vehicle.lastHeartbeatAt)}
              </span>
              {vehicle.state === 'released' || vehicle.state === 'refused' ? (
                <button
                  type="button"
                  onClick={() => void adopt(vehicle)}
                  title={vehicle.reason ?? undefined}
                  className="font-bold text-white/80"
                >
                  ADOPT
                </button>
              ) : null}
            </div>
          ))}
          {mesh.vehicles
            .filter((vehicle) => vehicle.reason !== null && vehicle.state === 'refused')
            .map((vehicle) => (
              <div key={`r${vehicle.sysid}`} className="text-[10px]" style={{ color: WARN }}>
                sys {vehicle.sysid}: {vehicle.reason}
              </div>
            ))}
        </div>
      ) : null}

      {error ? (
        <div className="font-mono text-[10px]" style={{ color: WARN }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default MeshPanel;
